import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, ListTodo, Users2, ClipboardCheck,
  FileText, Briefcase, LogOut, ShieldCheck, Menu, X, MessagesSquare, BarChart3
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import ChatBell from "@/components/ChatBell";
import AttendanceGate from "@/components/AttendanceGate";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/tasks", label: "Tasks", icon: ListTodo, testId: "nav-tasks" },
  { to: "/leads", label: "Leads", icon: Briefcase, testId: "nav-leads" },
  { to: "/attendance", label: "Attendance", icon: ClipboardCheck, testId: "nav-attendance" },
  { to: "/documents", label: "Documents", icon: FileText, testId: "nav-documents" },
  { to: "/chat", label: "Team Chat", icon: MessagesSquare, testId: "nav-chat" },
  { to: "/mis", label: "Performance MIS", icon: BarChart3, testId: "nav-mis" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const close = () => setMobileOpen(false);

  const sidebar = (
    <>
      <div className="px-6 py-6 border-b border-zinc-200 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <img
            src="https://customer-assets.emergentagent.com/job_task-leads-mgmt/artifacts/ngyuaae9_WhatsApp%20Image%202026-04-27%20at%2023.23.32.jpeg"
            alt="BizFinTax"
            className="h-9 w-9 object-contain shrink-0"
          />
          <div className="min-w-0">
            <div className="font-bold text-base tracking-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>BIZFINTAX</div>
            <div className="text-[9px] uppercase tracking-[0.08em] text-zinc-500 mt-0.5 leading-tight">An Initiative to Grow India</div>
          </div>
        </div>
        <button onClick={close} className="lg:hidden text-zinc-500" data-testid="close-sidebar">
          <X size={18} />
        </button>
      </div>

      <nav className="flex-1 py-4 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={close}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors border-l-2 ${
                  isActive
                    ? "bg-zinc-50 border-l-[#002FA7] text-zinc-900 font-medium"
                    : "border-l-transparent text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.75} />
              {item.label}
            </NavLink>
          );
        })}
        {user?.role === "admin" && (
          <NavLink
            to="/users"
            onClick={close}
            data-testid="nav-users"
            className={({ isActive }) =>
              `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors border-l-2 ${
                isActive
                  ? "bg-zinc-50 border-l-[#002FA7] text-zinc-900 font-medium"
                  : "border-l-transparent text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
              }`
            }
          >
            <Users2 size={16} strokeWidth={1.75} />
            Employees
          </NavLink>
        )}
      </nav>

      <div className="border-t border-zinc-200 px-6 py-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 bg-zinc-900 text-white flex items-center justify-center text-xs font-semibold">
            {user?.name?.[0] || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500">
              {user?.role === "admin" && <ShieldCheck size={10} />}
              {user?.role}
            </div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full flex items-center justify-center gap-2 text-xs uppercase tracking-wider py-2 border border-zinc-300 hover:bg-zinc-50 transition-colors"
        >
          <LogOut size={12} /> Logout
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-white text-zinc-900">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 border-r border-zinc-200 flex-col" data-testid="sidebar-desktop">
        {sidebar}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={close} />
          <aside className="lg:hidden fixed inset-y-0 left-0 w-72 bg-white z-50 flex flex-col border-r border-zinc-200" data-testid="sidebar-mobile">
            {sidebar}
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0 overflow-x-hidden" data-testid="main-content">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-zinc-200 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setMobileOpen(true)} data-testid="open-sidebar" className="p-2 border border-zinc-200">
            <Menu size={16} />
          </button>
          <div className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_task-leads-mgmt/artifacts/ngyuaae9_WhatsApp%20Image%202026-04-27%20at%2023.23.32.jpeg"
              alt="BizFinTax"
              className="h-7 w-7 object-contain"
            />
            <span className="font-bold text-sm tracking-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>BIZFINTAX</span>
          </div>
          <ChatBell />
          <NotificationBell />
        </div>

        {/* Desktop top bar with bells */}
        <div className="hidden lg:flex sticky top-0 z-30 bg-white border-b border-zinc-200 px-6 py-3 justify-end items-center gap-2">
          <ChatBell />
          <NotificationBell />
        </div>

        <AttendanceGate>
          <Outlet />
        </AttendanceGate>
      </main>
    </div>
  );
}
